import { useState, useEffect } from 'react';
import { getAllFunds, getStreamsByFund } from '../../../services/firestoreService';
import Table from '../../shared/UI/Table';
import Button from '../../shared/UI/Button';
import SelectField from '../../shared/Forms/SelectField';

export default function StreamList({ onAdd, onEdit }) {
  const [funds, setFunds] = useState([]);
  const [selectedFundId, setSelectedFundId] = useState('');
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getAllFunds().then(list => setFunds(list.filter(f => f.is_active !== false)));
  }, []);

  useEffect(() => {
    if (!selectedFundId) { setStreams([]); return; }
    setLoading(true);
    getStreamsByFund(selectedFundId)
      .then(setStreams)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [selectedFundId]);

  const selectedFund = funds.find(f => f.id === selectedFundId);
  const streamsEnabled = selectedFund?.has_streams === true;

  const fundOptions = funds.map(f => ({ value: f.id, label: f.name }));

  const columns = [
    { key: 'name', label: 'Stream Name' },
    { key: 'short_name', label: 'Short Name' },
    {
      key: 'territories',
      label: 'Territories',
      render: row => (row.territories || []).join(', ') || '—',
    },
    { key: 'base_currency', label: 'Base Currency' },
    {
      key: 'actions',
      label: 'Actions',
      render: row => (
        <Button size="sm" variant="secondary" onClick={() => onEdit(row, selectedFundId)}>Edit</Button>
      ),
    },
  ];

  if (error) return <div className="alert alert-error">{error}</div>;

  return (
    <div className="card">
      <div className="card-header">
        <h2>Fund Streams</h2>
        <Button
          variant="primary"
          onClick={() => onAdd(selectedFundId)}
          disabled={!selectedFundId || !streamsEnabled}
        >
          + Add New Stream
        </Button>
      </div>
      <div className="card-body">
        <div style={{ maxWidth: 360, marginBottom: 20 }}>
          <SelectField
            label="Select Fund"
            value={selectedFundId}
            onChange={e => setSelectedFundId(e.target.value)}
            options={fundOptions}
            placeholder="Choose a fund…"
          />
        </div>
        {selectedFundId && !streamsEnabled && (
          <div className="alert alert-info">
            This fund does not have streams enabled. Enable "Has Streams" on the fund to manage streams.
          </div>
        )}
        {selectedFundId && streamsEnabled && (
          <Table
            columns={columns}
            data={streams}
            loading={loading}
            emptyTitle="No streams configured"
            emptyMessage="Add a stream for this fund."
            emptyAction={{ label: '+ Add Stream', onClick: () => onAdd(selectedFundId) }}
          />
        )}
        {!selectedFundId && (
          <div className="empty-state">
            <div className="empty-state-icon">🌊</div>
            <h3>Select a fund</h3>
            <p>Choose a fund above to view and manage its streams.</p>
          </div>
        )}
      </div>
    </div>
  );
}
